export interface DynamicField {
  id: string;
  name: string;
  placeholder: string;
}

export interface Contact {
  id: string;
  name: string;
  details: string;
  createdAt: string;
}

export type TemplateKind = 'standard' | 'alumni' | 'combo' | 'zoom';

export function normalizeTemplateKind(kind?: string | null): TemplateKind {
  if (kind === 'alumni' || kind === 'combo' || kind === 'zoom') return kind;
  return 'standard';
}

export interface EmailTemplate {
  id: string;
  name: string;
  /** Part 1 HTML (same as htmlBodies[0]). */
  htmlBody: string;
  /** Per-email HTML, aligned with subjectLines. */
  htmlBodies: string[];
  subjectLines: string[];
  defaultSegmentId: number | null;
  kind: TemplateKind;
  createdAt: string;
  updatedAt: string;
}

export interface EventCampaignLink {
  id: string;
  hostlinksEventId: string;
  templateId: string | null;
  templateName: string | null;
  segmentId: number;
  segmentName: string | null;
  newsletterId: number;
  jobId: number | null;
  campaignName: string | null;
  createdAt: string;
  createdByUserId: string | null;
}

export interface ScheduledEmail {
  id: string;
  templateId: string;
  subjectLine: string;
  scheduledDate: string;
  scheduledTime: string; // Format: "HH:mm" (24-hour format)
  dynamicContent: Record<string, string>;
}

export interface SMTPSettings {
  host: string;
  port: number;
  secure: boolean;
  user: string;
  password: string;
  fromEmail: string;
}

/** SMTP.com API delivery stats (dashboard chart) — not the nodemailer relay. */
export interface SmtpComSettings {
  apiKey: string;
  enabled: boolean;
  /** Rolling window for the dashboard bounce chart */
  chartMonths?: 24 | 36;
}

/** Hostlinks WordPress email-events API. Key stays on the server. */
export interface HostlinksSettings {
  enabled: boolean;
  apiKey: string;
  baseUrl: string;
  /** Shared secret for plugin POST to EmailCraft (not the WP X-HL-Key). */
  ingestKey?: string;
  ingestUrl?: string;
}

export interface HostlinksEvent {
  id: string;
  location: string;
  city: string;
  state: string;
  start: string;
  end: string;
  dates_display: string;
  type: string;
  type_abbr: string;
  marketer: string;
  instructor: string;
  is_zoom: boolean;
  reg_url: string;
  web_url: string;
  /** Venue host organization (Hostlinks “Host Name”) — fills {{INTRO}}. */
  host_name?: string;
}

export interface ZoomPreviewEvent {
  id: string;
  location: string;
  dates_display: string;
  type: string;
  type_abbr: string;
  web_url: string;
  start: string;
}

export interface ZoomPreviewSend {
  sendIndex: 1 | 2 | 3;
  weekday: 'Tuesday' | 'Wednesday' | 'Thursday';
  segmentName: string;
  segmentId: number | null;
  campaignName: string;
  date: string;
  time: string;
}

export interface ZoomPreview {
  templateId: string;
  templateName: string;
  subject: string;
  html: string;
  buckets: {
    writing: ZoomPreviewEvent[];
    management: ZoomPreviewEvent[];
    sub: ZoomPreviewEvent[];
  };
  skipped: ZoomPreviewEvent[];
  sends: ZoomPreviewSend[];
  errors: string[];
  warning?: string;
}

export interface ZoomQueueSendResult {
  sendIndex: 1 | 2 | 3;
  segmentName: string;
  segmentId: number;
  campaignName?: string;
  ok: boolean;
  newsletterId?: number;
  jobId?: number;
  error?: string;
}

export interface EventScheduleRowResult {
  index: number;
  subject?: string;
  status: 'ok' | 'failed' | 'skipped';
  newsletterId?: number;
  jobId?: number;
  error?: string;
}

export interface EventBuildResult {
  templateId: string;
  segmentId: number;
  ok: boolean;
  /** True when some schedule rows queued before a later row failed. */
  partial?: boolean;
  newsletterIds?: number[];
  jobIds?: number[];
  scheduleResults?: EventScheduleRowResult[];
  error?: string;
}

export interface MySQLSettings {
  host: string;
  port: number;
  user: string;
  password: string;
  database: string;
  enabled: boolean;
}

export interface InterspireSettings {
  host: string;
  port: number;
  user: string;
  password: string;
  database: string;
  enabled: boolean;
  sendHistoryCacheMins?: number;  // minutes to cache the Sends list (default 60)
  queueCacheMins?: number;        // minutes to cache the Email Queue list (default 60)
}

export interface EmailConfigSettings {
  sendFromName: string;
  sendFromEmail: string;
  replyToEmail: string;
  bounceEmail: string;
}

export interface PreviewEmail {
  address: string;
}

export type UserRole = 'admin' | 'manager' | 'user';

export interface User {
  id: string;
  username: string;
  email: string;
  role: UserRole;
  createdAt: string;
  hasPassword?: boolean; // from API when loading profile
}

export interface RoleFeatures {
  buildCampaign: boolean;
  contacts: boolean;
  emailTemplates: boolean;
  alumniImport: boolean;
  alerts: boolean;
  emailSends: boolean;
  emailQueue: boolean;
  // Contacts sub-features
  contactsQuickActions: boolean;
  contactsBuildSegment: boolean;
  contactsEditSegment: boolean;
  contactsDeleteSegment: boolean;
  contactsImport: boolean;
  contactsManageSubscribers: boolean;
  domainLookup: boolean;
  dashboard: boolean;
  /** Click a month on the Interspire unsub chart to see elevated sends */
  dashboardUnsubDrilldown: boolean;
  // Queue sub-features
  queueReviewButtons: boolean;
  queueSelectionCheckmarks: boolean;
  queueReviewModal: boolean;
  queueEditEmail: boolean;
  queueStaggerButton: boolean;
  metricsLogs: boolean;
  sendsExportRecipients: boolean;
  contactsExportList: boolean;
  contactsExportSegment: boolean;
  contactsListScoring: boolean;
  contactsAuditSegments: boolean;
  contactsUtilities: boolean;
}

export interface SegmentVerification {
  segmentId: number;
  segmentName: string;
  verifiedAt: string;
  verifiedByUserId: string;
  verifiedByUsername: string;
  valueCount: number;
  valuesHash: string;
}

export interface FeaturePermissions {
  manager: RoleFeatures;
  user: RoleFeatures;
}

export interface InterspireList {
  listid: number;
  name: string;
}

export interface InterspireSegment {
  segmentid: number;
  segmentname: string;
  searchinfo: string;
  listid?: number;
}

export interface UnifiedDomainResult {
  db: { total: number; unsubscribed: number; bounced: number; active: number };
  segment: {
    segmentName: string;
    listTotal: number;
    listUnsubscribed: number;
    listBounced: number;
    listActive: number;
    inSegment: number;
    notInSegment: number;
  } | null;
  sent: number | null;
  explanation: string;
  sampleContacts?: { emailaddress: string; unsubscribed: number; bounced: number }[];
  ruleFields?: { fieldId: number; fieldName: string; allowedValues: string[] }[];
  excludedByRules?: { email: string; fieldValues: { fieldId: number; fieldName: string; value: string | null }[] }[];
  missingByList?: { listId: number; listName: string; count: number; unsubscribed: number; bounced: number; active: number }[];
}

export interface SegmentDomainResult {
  segmentName: string;
  stats: {
    total: number;
    active: number;
    inSegment: number;
    notInSegment: number;
    unsubscribed: number;
    bounced: number;
  };
  explanation: string;
  sampleContacts?: { emailaddress: string; unsubscribed: number; bounced: number }[];
}

export interface AlertPreferences {
  userId: string;
  enabled: boolean;
  lookbackDays: number;
  unsubscribePerSend: { enabled: boolean; threshold: number };
  unsubscribeDomain:  { enabled: boolean; threshold: number };
  bounceRate:         { enabled: boolean; threshold: number };
  stuckSend?:         { enabled: boolean; thresholdHours: number };
  pausedSend?:        { enabled: boolean };
}

export interface AlertRecord {
  id: string;
  userId: string;
  type: 'unsubscribe_per_send' | 'unsubscribe_domain' | 'bounce_rate' | 'stuck_send' | 'paused_send';
  triggeredAt: string;
  campaignName?: string;
  detail: string;
  emailSent: boolean;
}

export interface StaggerScheduleSettings {
  staggerCronEnabled: boolean;
  staggerRunHour: number;
  staggerIntervalMinutes: 15 | 30 | 45 | 60;
}

export interface StaggerChange {
  jobid: number;
  campaignName: string;
  pacificDate: string;
  oldScheduledDate: number;
  newScheduledDate: number;
}

export interface StaggerResult {
  daysAffected: number;
  jobsUpdated: number;
  changes: StaggerChange[];
  warnings: string[];
}

export interface EmailSendRow {
  statid: number;
  newsletterid: number;
  campaignName: string;
  listName: string | null;
  segmentId: number | null;
  segmentName: string | null;
  startDate: number | null;   // Unix timestamp (seconds)
  endDate: number | null;     // Unix timestamp (seconds)
  recipientsCount: number;
}

export interface EmailQueueRow {
  jobid: number;
  newsletterid: number;
  campaignName: string;
  listName: string | null;
  segmentId: number | null;
  segmentName: string | null;
  createdDate: number | null;   // Unix timestamp (seconds) — newsletter createdate
  scheduledDate: number | null; // Unix timestamp (seconds)
  status: 'scheduled' | 'sending' | 'paused';
  approved: boolean;
  authorised: boolean;
}

export interface DomainContactSample {
  emailaddress: string;
  unsubscribed: number;
  bounced: number;
}

export interface DomainInvestigationResult {
  stats: {
    total: number;
    sent: number;
    unsubscribed: number;
    bounced: number;
    eligible: number;
  };
  explanation: string;
  sampleContacts?: DomainContactSample[];
}

export interface BrandingSettings {
  logoDataUrl: string;
  footerText: string;
}

export interface ExecutiveReportSettings {
  enabled: boolean;
  periodDays: 30 | 60 | 90;
  dayOfWeek: number;
  hour: number;
  timezone: string;
  recipients: string[];
  lastRunAt?: string;
  lastRunStatus?: 'ok' | 'error' | 'skipped';
  lastRunError?: string;
}

export interface UpdatedSegmentsReportSettings {
  enabled: boolean;
  hour: number;
  timezone: string;
  recipients: string[];
  lastRunAt?: string;
  lastRunStatus?: 'ok' | 'error' | 'skipped' | 'no_changes';
  lastRunError?: string;
}

export interface ReportSettings {
  executive: ExecutiveReportSettings;
  updatedSegments: UpdatedSegmentsReportSettings;
}

export interface SendStats {
  statid: number;
  campaignName: string;
  listName: string | null;
  segmentName: string | null;
  startDate: number | null;     // Unix timestamp (seconds)
  endDate: number | null;       // Unix timestamp (seconds)
  recipientsCount: number;
  sendSize: number;
  unsubscribes: number;
  bounceSoft: number;
  bounceHard: number;
  bounceUnknown: number;
  totalBounces: number;
  opened: number | null;        // null = not tracked on this install
  unopened: number | null;
  clicked: number | null;
  forwarded: number | null;
}

export interface SendRecipientRow {
  email: string;
  firstName: string;
  lastName: string;
}

export interface DashboardRecentCampaign {
  statid: number;
  campaignName: string;
  startDate: number | null;
  recipientsCount: number;
  openRate: number | null;
  clickRate: number | null;
  bounceRate: number | null;
}

export interface DashboardSummary {
  period: { days: number; from: number; to: number };
  aggregate: {
    totalSends: number;
    totalRecipients: number;
    avgOpenRate: number | null;
    avgClickRate: number | null;
    avgBounceRate: number | null;
    avgUnsubRate: number | null;
  };
  recentCampaigns: DashboardRecentCampaign[];
  queueSummary: {
    total: number;
    scheduled: number;
    paused: number;
    aiPassed: number;
    aiFailed: number;
    aiOverridden: number;
    aiNotRun: number;
  };
}

export type PostfixHealth = 'green' | 'yellow' | 'red' | 'unknown';

export interface PostfixCompactStats {
  postfix_queue_count: number;
  postfix_queue_kbytes: number;
  /** @deprecated Prefer rate_60m_per_hour */
  rate_5m_per_hour: number;
  /** Actual SMTP.com accepts in the last 60 minutes */
  rate_60m_per_hour: number;
  serious_errors_15m: number;
  postfix_status: string;
  active_jobs_count: number;
  health: PostfixHealth;
  last_updated: string;
}

export interface PostfixHistoricalSummary {
  range: string;
  range_minutes: number;
  total_sent: number;
  avg_rate_per_hour: number;
  peak_rate_per_hour: number;
  total_serious_errors: number;
  avg_queue: number;
  peak_queue: number;
  snapshot_count: number;
}

export interface PostfixSnapshot {
  id: number;
  captured_at: string;
  postfix_service_status: string;
  postfix_queue_count: number;
  postfix_queue_kbytes: number;
  sent_5m: number;
  sent_15m: number;
  sent_60m: number;
  rate_5m_per_hour: number;
  rate_15m_per_hour: number;
  rate_60m_per_hour: number;
  deferred_15m: number;
  bounced_15m: number;
  expired_15m: number;
  serious_errors_15m: number;
  auth_errors_15m: number;
  active_jobs_json: string | null;
  disk_free_gb: number | null;
  disk_used_percent: number | null;
  overall_health: PostfixHealth;
}

export interface PostfixActiveJob {
  jobid: number;
  st: string;
  queueid: number;
  newsletterid: number;
  jobtime: string;
  total: number;
  processed: number;
  unprocessed: number;
  pct: number;
  eta_hours: number | null;
}

export interface PostfixOverview extends PostfixCompactStats {
  sent_5m: number;
  sent_15m: number;
  sent_60m: number;
  rate_15m_per_hour: number;
  rate_60m_per_hour: number;
  deferred_15m: number;
  bounced_15m: number;
  expired_15m: number;
  disk_free_gb: number | null;
  disk_used_percent: number | null;
  target_hourly_rate: number;
  eta_100k_hours: number | null;
  health_reason: string;
  active_jobs: PostfixActiveJob[];
}

export interface PostfixEvent {
  id: number;
  log_timestamp: string;
  queue_id: string | null;
  event_type: string;
  recipient: string | null;
  recipient_domain: string | null;
  relay: string | null;
  dsn: string | null;
  status: string | null;
  delay_seconds: number | null;
  delays_text: string | null;
  tracking_id: string | null;
  raw_line: string;
}

export interface PostfixError {
  id: number;
  log_timestamp: string;
  severity: string;
  category: string;
  queue_id: string | null;
  recipient: string | null;
  message: string | null;
  raw_line: string;
  ignored_by_default: boolean;
}

export interface PostfixDomainRow {
  domain: string;
  sent_count: number;
  deferred_count: number;
  bounced_count: number;
  expired_count: number;
  avg_delay: number | null;
  max_delay: number | null;
  pct_of_volume: number;
}

export interface PostfixMonitorSettings {
  target_hourly_rate: number;
  queue_warning_count: number;
  queue_critical_count: number;
  disk_warning_gb: number;
  disk_critical_gb: number;
  refresh_seconds: number;
  retention_snapshots_days: number;
  retention_events_days: number;
  retention_errors_days: number;
  serious_error_threshold: number;
  smtp_destination_rate_delay_ms: number;
  smtp_destination_concurrency_limit: number;
  smtp_destination_recipient_limit: number;
  smtp_pacing_live?: {
    concurrency: number;
    delayMs: number;
    delayRaw: string;
    recipients: number;
  } | null;
  smtp_pacing_applied?: boolean;
  smtp_pacing_message?: string | null;
  smtp_rate_delay_live?: string | null;
  smtp_rate_delay_applied?: boolean;
  smtp_rate_delay_message?: string | null;
}